import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "../components/NumInput";

import { ApiError, type ErpConfigRow, api } from "../api/client";

const { Title, Paragraph } = Typography;

export const ErpSettingsPage = () => {
  const [config, setConfig] = useState<ErpConfigRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [pendingMassRevoke, setPendingMassRevoke] = useState<string | null>(null);
  const [form] = Form.useForm<{
    enabled: boolean;
    base_url: string;
    auth_type: string;
    auth_value: string;
    sync_interval_minutes: number;
  }>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await api.getErpConfig();
      setConfig(cfg);
      form.setFieldsValue({
        enabled: cfg.enabled,
        base_url: cfg.base_url ?? "",
        auth_type: cfg.auth_type ?? "bearer",
        auth_value: "",
        sync_interval_minutes: cfg.sync_interval_minutes,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const patch = { ...values };
      if (!patch.auth_value) delete (patch as { auth_value?: string }).auth_value;
      await api.updateErpConfig(patch);
      setEditing(false);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  };

  const onSyncNow = async (force = false) => {
    try {
      const r = await api.syncErpNow(force);
      if (r.aborted_reason) {
        setPendingMassRevoke(r.aborted_reason);
      } else if (r.error) {
        setError(r.error);
      } else {
        setInfo(`Sync ok: +${r.rows_added} ~${r.rows_updated} -${r.rows_removed}`);
      }
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <Card loading={loading}>
      <Title level={2} style={{ marginBottom: 8 }}>
        Snipe-IT integration
      </Title>
      <Paragraph type="secondary">
        Optional. Maps scanned QR/badge barcodes to Snipe-IT user IDs.
        When enabled, barcodes not in the local cache trigger a live
        lookup against your Snipe-IT API; a periodic sync reconciles
        the full snapshot. (The integration is plug-in driven —
        ``auth_type`` selects the adapter; the supported one is
        ``snipeit``.)
      </Paragraph>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}
      {info && (
        <Alert
          type="success"
          message={info}
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setInfo(null)}
        />
      )}
      {pendingMassRevoke && (
        <Alert
          type="warning"
          message="Snipe-IT sync aborted — mass-revoke guard fired"
          description={`${pendingMassRevoke}. Review the diff in Snipe-IT, then click "Apply anyway" to override.`}
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button
              size="small"
              danger
              onClick={async () => {
                await onSyncNow(true);
                setPendingMassRevoke(null);
              }}
            >
              Apply anyway
            </Button>
          }
          closable
          onClose={() => setPendingMassRevoke(null)}
        />
      )}
      {config && (
        <>
          <Descriptions
            column={2}
            size="small"
            bordered
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label="Enabled">
              {config.enabled ? (
                <Tag color="green">on</Tag>
              ) : (
                <Tag color="default">off</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Auth type">
              {config.auth_type ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Base URL">
              {config.base_url ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Credential">
              {config.has_credential ? "set" : "not set"}
            </Descriptions.Item>
            <Descriptions.Item label="Sync interval">
              every {config.sync_interval_minutes} min
            </Descriptions.Item>
            <Descriptions.Item label="Snipe-IT-source rows">
              {config.erp_row_count}
            </Descriptions.Item>
            <Descriptions.Item label="Last sync">
              {config.last_sync ? new Date(config.last_sync).toLocaleString() : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Last status">
              {config.last_status ?? "—"}
            </Descriptions.Item>
          </Descriptions>
          <Space>
            <Button onClick={() => setEditing(true)}>Edit config</Button>
            <Button
              type="primary"
              disabled={!config.enabled}
              onClick={() => onSyncNow(false)}
            >
              Sync now
            </Button>
          </Space>
        </>
      )}
      <Modal
        title="Snipe-IT configuration"
        open={editing}
        onOk={onSave}
        onCancel={() => setEditing(false)}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Base URL" name="base_url">
            <Input placeholder="https://snipe.example.com/api/v1" />
          </Form.Item>
          <Form.Item label="Auth type" name="auth_type">
            <Input placeholder="snipeit" />
          </Form.Item>
          <Form.Item
            label="API token (leave blank to keep existing)"
            name="auth_value"
          >
            <Input.Password />
          </Form.Item>
          <Form.Item label="Sync interval (minutes)" name="sync_interval_minutes">
            <InputNumber min={1} max={10080} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
